import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { createAction, createReducer } from '@reduxjs/toolkit'
import {
  AudioQuality,
  AudioStatus,
  getRecordingPermissionsAsync,
  IOSOutputFormat,
  RecorderState,
  RecordingOptions,
  RecordingOptionsWeb,
  RecordingStatus,
  requestRecordingPermissionsAsync,
  setAudioModeAsync,
  useAudioPlayer,
  useAudioPlayerStatus,
  useAudioRecorder,
  useAudioRecorderState,
} from 'expo-audio'
import React, { useEffect, useState } from 'react'
import { Platform, StyleSheet, TouchableHighlight } from 'react-native'
import { Dialog, Paragraph } from 'react-native-paper'
import { useDispatch, useSelector } from 'react-redux'
import { Text, View } from '~components/Themed'
import { MAX_RECORDING_LENGTH_MS } from '~constants/Settings'
import { setLockedSection, unlockSection, useChatContext } from '~context/ChatProvider'
import { ApplicationState } from '~redux'
import { removeChatAudio, setChatAudio } from '~redux/chat/actions'
import { Theme } from '~theme'
import { useTheme } from '~theme/ThemeManager'
import { AudioRecordingType } from '~types'
import { AlertConfirmation } from './AlertConfirmation'

export interface AudioRecordingProps {
  type: AudioRecordingType
  index: number
  source?: { uri: string }
  expanded: boolean
  readonly: boolean
}

export interface State {
  // Recording
  isRecording: boolean
  isRecordingPaused: boolean
  abandonRecording: boolean

  // Playback
  isPlaying: boolean
  isPlaybackPaused: boolean
  playbackURI?: string
}

export const initialState: State = {
  isRecording: false,
  isRecordingPaused: false,
  abandonRecording: false,

  isPlaying: false,
  isPlaybackPaused: false,
}

const setPlaybackStatus = createAction<AudioStatus>('setPlaybackStatus')
const setRecordingStatus = createAction<RecordingStatus>('setRecordingStatus')
const setRecorderState = createAction<RecorderState>('setRecorderState')
const recording = createAction<void>('recording')
const abandonRecording = createAction<void>('abandonRecording')

const reducer = createReducer(initialState, (builder) =>
  builder
    .addCase(setPlaybackStatus, (state, action) => {
      const status = action.payload
      if (status.isLoaded) {
        state.isPlaying = status.playing
        state.isPlaybackPaused = false
        if (status.didJustFinish) {
          // After stop
        } else if (!status.playing) {
          // If isLoaded but not playing
          state.isPlaybackPaused = true
        }
      } else {
        // Unloaded
        state.isPlaying = false
        state.isPlaybackPaused = false
        state.playbackURI = undefined
      }
    })
    .addCase(setRecordingStatus, (state, action) => {
      const status = action.payload
      if (status.isFinished) {
        // Recording finished
        state.isRecording = false
        state.isRecordingPaused = false
        state.playbackURI = status.url ?? undefined
      }
    })
    .addCase(setRecorderState, (state, action) => {
      const status = action.payload
      state.isRecording = status.isRecording
      state.isRecordingPaused = !status.isRecording && status.durationMillis > 0
    })
    .addCase(recording, (state) => {
      state.abandonRecording = false
    })
    .addCase(abandonRecording, (state) => {
      if (state.isRecording) {
        state.abandonRecording = true
      }
    }),
)

export const recordingSettings: RecordingOptions & {
  web?: RecordingOptionsWeb & { extension: string }
} = {
  extension: '.m4a',
  sampleRate: 44100,
  numberOfChannels: 2,
  bitRate: 64000,
  android: {
    outputFormat: 'mpeg4',
    audioEncoder: 'he_aac',
  },
  ios: {
    outputFormat: IOSOutputFormat.MPEG4AAC_HE,
    audioQuality: AudioQuality.MEDIUM,
    linearPCMBitDepth: 16,
    linearPCMIsBigEndian: false,
    linearPCMIsFloat: false,
  },
  ...(Platform.OS === 'web' && {
    web: [
      {
        mimeType: 'audio/mp4;codecs=mp4a.40.2', // MPEG-4 AAC LC
        bitsPerSecond: 64000,
        extension: '.m4a',
      },
      {
        mimeType: 'audio/webm;codecs="opus"',
        bitsPerSecond: 64000,
        extension: '.webm',
      },
      {
        mimeType: 'audio/webm',
        bitsPerSecond: 64000,
        extension: '.webm',
      },
    ].find((mime) => (window as any).MediaRecorder?.isTypeSupported(mime.mimeType)),
  }),
}

const getMMSSFromSeconds = (totalSeconds: number) => {
  const seconds = Math.floor(totalSeconds % 60)
  const minutes = Math.floor(totalSeconds / 60)

  const padWithZero = (number: number) => {
    const string = number.toString()
    if (number < 10) {
      return `0${string}`
    }
    return string
  }
  return `${padWithZero(minutes)}:${padWithZero(seconds)}`
}

export default function AudioRecording({
  type,
  index,
  source,
  expanded,
  readonly,
}: AudioRecordingProps): React.JSX.Element {
  const [stateChat, dispatchChat] = useChatContext()
  const [state, dispatch] = React.useReducer(reducer, initialState)
  const { chat } = useSelector((state: ApplicationState) => state.chat)
  const globalDispatch = useDispatch()

  const lock = React.useCallback(() => dispatchChat(setLockedSection(index)), [dispatchChat, index])
  const unlock = React.useCallback(() => dispatchChat(unlockSection(index)), [dispatchChat, index])

  const processAudio = React.useCallback(
    async (uri: string) => {
      if (state.abandonRecording) {
        return
      }

      globalDispatch(
        setChatAudio({
          chat: { id: chat.id, path: chat.path },
          index,
          uri,
          extension: recordingSettings.web?.extension ?? recordingSettings.extension,
          type,
        }),
      )
    },
    [chat.id, chat.path, globalDispatch, index, type, state.abandonRecording],
  )

  const player = useAudioPlayer(source)
  const playerStatus = useAudioPlayerStatus(player)
  const recorder = useAudioRecorder(recordingSettings, async (status: RecordingStatus) => {
    dispatch(setRecordingStatus(status))

    if (status.isFinished && status.url && !state.abandonRecording) {
      console.log('Recording finished, processing audio...')
      if (Platform.OS !== 'web') {
        // blob
        player.replace({ uri: status.url })
      }
      await processAudio(status.url)
    }

    unlock()
  })

  useEffect(() => {
    dispatch(setPlaybackStatus(playerStatus))

    const action = async () => {
      // Rewind if just finished
      if (playerStatus.didJustFinish) {
        await player.seekTo(0)
        unlock()
      }
    }
    action()
  }, [player, playerStatus, stateChat.lockedSection, unlock])

  useEffect(() => {
    const allowsRecording = async () => {
      await setAudioModeAsync({
        allowsRecording: !readonly,
        playsInSilentMode: true,
      })
    }
    allowsRecording()
  }, [readonly])

  const recorderState = useAudioRecorderState(recorder)

  const [isLoading, setIsLoading] = useState(false)
  const { theme } = useTheme()
  const styles = getStyles(theme)
  const detailsStyle = type === 'teacher' ? styles.detailsLeft : styles.detailsRight
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)

  const hasSource = source !== undefined && source !== null
  const unlocked = stateChat.lockedSection === -1
  const hasLock = stateChat.lockedSection === index

  const stopRecordingAndEnablePlayback = React.useCallback(async () => {
    if (isLoading) {
      return
    }

    if (!state.isRecording && !state.isRecordingPaused) {
      return
    }

    setIsLoading(true)

    try {
      await recorder.stop()
    } catch (error) {
      // Do nothing -- we are already unloaded.
      console.log('Error stopping recording:', error)
    }

    setIsLoading(false)
  }, [isLoading, recorder, state.isRecording, state.isRecordingPaused])

  useEffect(() => {
    dispatch(setRecorderState(recorderState))

    if (recorderState.isRecording && recorderState.durationMillis > MAX_RECORDING_LENGTH_MS) {
      const action = async () => {
        console.warn('Recording exceeded maximum length, stopping...')
        await stopRecordingAndEnablePlayback()
      }

      action()
    }
  }, [recorderState, state.isRecording, stopRecordingAndEnablePlayback])

  const onRecordPressed = async () => {
    if (Platform.OS === 'web' && recordingSettings.web === undefined) {
      alert(
        'Browser is not compatible with audio recording, please ensure you are using Chrome, Firefox, Edge or Safari.',
      )
      return
    }

    const { status } = await getRecordingPermissionsAsync()

    if (status !== 'granted') {
      // Permission not granted, request it
      const { granted } = await requestRecordingPermissionsAsync()
      if (!granted) {
        alert('Permission to record audio is required!')
        return
      }
    }

    if (state.isRecording) {
      pauseRecording()
    } else if (state.isRecordingPaused) {
      resumeRecording()
    } else if (!state.isRecording) {
      stopPlaybackAndBeginRecording()
    }
  }

  const onPlayPausePressed = async () => {
    if (source === undefined) {
      return
    }

    try {
      if (player.paused) {
        lock()
        player.play()
      } else {
        player.pause()
        unlock()
      }
    } catch (error) {
      // An error occurred!
      console.warn(error)
    }
  }

  const onStopPressed = async () => {
    if (state.isRecording || state.isRecordingPaused) {
      await stopRecordingAndEnablePlayback()
    } else if (player.playing) {
      stopPlayback()
    } else if (player.currentTime > 0) {
      await player.seekTo(0)
    }
  }

  const trashAction = () => {
    globalDispatch(
      removeChatAudio({
        chat: { id: chat.id, path: chat.path },
        index,
        type,
      }),
    )
  }

  const prepareRecording = async () => {
    lock()

    if (!recorder.getStatus().canRecord) {
      if (Platform.OS === 'web') {
        console.log('Preparing to record with mimeType: ' + recordingSettings.web?.mimeType)
      }
      try {
        await recorder.prepareToRecordAsync()
      } catch (error) {
        console.warn(error)
        unlock()
      }
    }
  }

  const stopPlaybackAndBeginRecording = async () => {
    if (isLoading || recorder.isRecording) {
      return
    }

    setIsLoading(true)

    await stopPlayback()

    await prepareRecording()

    try {
      if (recorder.getStatus().canRecord) {
        recorder.record() // In expo-audio 0.4.6, recordForDuration acts strange if manually stopped before duration
        // dispatch(recording())
      } else {
        console.log('Recorder is not ready to record')
      }
    } catch (e) {
      console.warn(e)
    }

    setIsLoading(false)
  }

  const stopPlayback = React.useCallback(() => {
    if (player.playing) {
      try {
        player.pause()
      } catch (error) {
        console.log('Error stopping playback:', error)
      }
    }
    unlock()
  }, [player, unlock])

  const pauseRecording = () => {
    setIsLoading(true)

    if (recorder.isRecording) {
      try {
        recorder.pause()
      } catch (error) {
        console.log('Error pausing recording:', error)
      }
    }

    setIsLoading(false)
  }

  const resumeRecording = async () => {
    setIsLoading(true)

    if (recorderState.durationMillis > 0) {
      try {
        recorder.record()
      } catch (error) {
        console.log('Error resuming recording:', error)
      }
    }

    setIsLoading(false)
  }

  if (!expanded) {
    return <></>
  }

  return (
    <View style={styles.containerDetails}>
      <View style={detailsStyle}>
        {/* TODO: reuse same button */}
        {!hasSource && (
          <TouchableHighlight
            onPress={onRecordPressed}
            style={styles.button}
            disabled={readonly || isLoading || !(unlocked || hasLock)}
          >
            <View style={styles.buttonBackground}>
              <FontAwesomeIcon
                color={unlocked || hasLock ? theme.primary : theme[300]}
                size={22}
                icon={
                  state.isRecording && !state.isRecordingPaused
                    ? ['fas', 'pause']
                    : ['fas', 'microphone']
                }
              />
            </View>
          </TouchableHighlight>
        )}
        {hasSource && (
          <TouchableHighlight
            onPress={onPlayPausePressed}
            style={styles.button}
            disabled={!(unlocked || hasLock)}
          >
            <View style={styles.buttonBackground}>
              <FontAwesomeIcon
                color={unlocked || hasLock ? theme.primary : theme[300]}
                size={22}
                icon={state.isPlaying ? ['fas', 'pause'] : ['fas', 'play']}
              />
            </View>
          </TouchableHighlight>
        )}
        {(state.isRecording || hasSource || state.isRecordingPaused || hasLock) && (
          <Text style={styles.timestamp}>
            {state.isRecording || state.isRecordingPaused
              ? getMMSSFromSeconds(recorderState.durationMillis / 1000) // recorder.currentTime is always 0 on web
              : getMMSSFromSeconds(player.currentTime)}
          </Text>
        )}
        {(state.isRecording || hasSource || state.isRecordingPaused || hasLock) && (
          <TouchableHighlight
            onPress={onStopPressed}
            style={styles.button}
            disabled={
              isLoading ||
              !(
                state.isPlaying ||
                state.isPlaybackPaused ||
                state.isRecording ||
                state.isRecordingPaused
              )
            }
          >
            <View style={styles.buttonBackground}>
              <FontAwesomeIcon
                color={
                  !(
                    state.isPlaying ||
                    state.isPlaybackPaused ||
                    state.isRecording ||
                    state.isRecordingPaused
                  )
                    ? theme[300]
                    : theme.primary
                }
                size={22}
                icon={['fas', 'stop']}
              />
            </View>
          </TouchableHighlight>
        )}
        {(hasSource || state.isRecordingPaused) && (
          <TouchableHighlight
            onPress={() => setShowDeleteConfirmation(true)}
            style={[styles.button, { marginLeft: 8 }]}
            disabled={readonly || isLoading || !unlocked || !hasSource}
          >
            <View style={styles.buttonBackground}>
              <FontAwesomeIcon
                color={readonly || !unlocked || !hasSource ? theme[300] : theme.primary}
                size={22}
                icon={['far', 'trash-alt']}
              />
            </View>
          </TouchableHighlight>
        )}
        {showDeleteConfirmation && (
          <AlertConfirmation
            visible={showDeleteConfirmation}
            setVisible={setShowDeleteConfirmation}
            confirmAction={trashAction}
          >
            <Dialog.Title>Delete audio recording?</Dialog.Title>
            <Dialog.Content>
              <Paragraph>Are you sure you want to delete this audio recording?</Paragraph>
            </Dialog.Content>
          </AlertConfirmation>
        )}
      </View>
    </View>
  )
}

export const avatarSize = 64

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    detailsLeft: {
      borderWidth: 1,
      borderColor: theme[200],
      paddingHorizontal: 8,
      marginLeft: avatarSize / 2,
      paddingLeft: avatarSize / 2 + 8,
      height: avatarSize,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderTopRightRadius: avatarSize / 3,
      borderBottomRightRadius: avatarSize / 3,
      flexDirection: 'row',
      backgroundColor: theme[100],
    },
    detailsRight: {
      borderWidth: 1,
      borderColor: theme[200],
      paddingHorizontal: 8,
      marginRight: avatarSize / 2,
      paddingRight: avatarSize / 2 + 8,
      height: avatarSize,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderTopLeftRadius: avatarSize / 3,
      borderBottomLeftRadius: avatarSize / 3,
      flexDirection: 'row',
      backgroundColor: theme[100],
    },
    containerDetails: {
      backgroundColor: 'transparent',
    },
    button: {
      borderRadius: 24,
    },
    buttonBackground: {
      borderRadius: 24,
      borderWidth: 1,
      padding: 8,
      borderColor: theme[300],
      backgroundColor: theme[200],
    },
    timestamp: {
      fontVariant: ['tabular-nums'],
      paddingHorizontal: 8,
    },
  })
